import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Radio, Users, MessageSquare, Zap, RefreshCw, Loader2, Send, User, LogIn, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Admin-only "Live now" board — pulses a green LED next to every user
 * actively on the site, plus a guest count. Nudge / Message send an in-app
 * notification straight to the client's bell.
 */
export default function AdminLive() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudge, setNudge] = useState({ open: false, user: null, text: "", sending: false });
  const [ticker, setTicker] = useState([]);
  const prevRef = useRef({ ids: null, users: [] });

  const load = async () => {
    try {
      const { data } = await api.get("/presence/live");
      setData(data);
      // Diff for the client-only ticker: only track role==="client" arrivals/departures
      const cur = (data?.users || []).filter((u) => u.role === "client");
      const curIds = new Set(cur.map((u) => u.user_id));
      if (prevRef.current.ids) {
        const now = Date.now();
        const arrivals   = cur.filter((u) => !prevRef.current.ids.has(u.user_id));
        const departures = prevRef.current.users.filter((u) => !curIds.has(u.user_id));
        const events = [
          ...arrivals.map((u)   => ({ id: `in-${u.user_id}-${now}`,  user: u, dir: "in",  at: now })),
          ...departures.map((u) => ({ id: `out-${u.user_id}-${now}`, user: u, dir: "out", at: now })),
        ];
        if (events.length) setTicker((t) => [...events, ...t].slice(0, 20));
      }
      prevRef.current = { ids: curIds, users: cur };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load live users.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const openNudge = (u) => setNudge({ open: true, user: u, text: "", sending: false });
  const sendNudge = async () => {
    if (!nudge.user) return;
    setNudge((n) => ({ ...n, sending: true }));
    try {
      await api.post("/presence/nudge", { user_id: nudge.user.user_id, text: nudge.text.trim() || undefined });
      toast.success(`Nudge sent to ${nudge.user.name || nudge.user.email}.`);
      setNudge({ open: false, user: null, text: "", sending: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge failed.");
      setNudge((n) => ({ ...n, sending: false }));
    }
  };

  const users = data?.users || [];
  const clients = users.filter((u) => u.role === "client");
  const staff = users.filter((u) => u.role !== "client");
  const guests = data?.guests || 0;
  const totalLive = users.length + guests;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-live-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-slate-400">Ops · Real-Time</p>
            <h1 className="text-3xl font-display font-black text-slate-900 flex items-center gap-2">
              <span className="relative inline-flex">
                <span className="absolute inline-flex h-3 w-3 rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
              </span>
              Live Now
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Everyone currently on Credit Quack. Message or nudge any signed-in client — the ping lands in their notification bell instantly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-sm font-black px-3 py-1.5" data-testid="live-total">
              {totalLive} LIVE
            </Badge>
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-live">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard icon={User} label="Live clients" value={clients.length} color="text-emerald-600" testid="stat-clients" />
          <StatCard icon={Users} label="Live team" value={staff.length} color="text-sky-600" testid="stat-staff" />
          <StatCard icon={Radio} label="Guests / anonymous" value={guests} color="text-amber-600" testid="stat-guests" />
        </div>

        {/* Live client ticker — arrivals/departures only */}
        <ClientTicker events={ticker} />

        {loading ? (
          <p className="text-slate-400 text-center py-10"><Loader2 className="inline animate-spin mr-1" size={14} /> Loading…</p>
        ) : (
          <>
            <Section title="Clients online now" empty="No clients on the site right now." rows={clients} onNudge={openNudge} />
            <Section title="Team on the site" empty="No teammates online right now." rows={staff} onNudge={openNudge} />
          </>
        )}
      </div>

      <Dialog open={nudge.open} onOpenChange={(o) => !o && setNudge({ open: false, user: null, text: "", sending: false })}>
        <DialogContent data-testid="nudge-dialog">
          <DialogHeader>
            <DialogTitle>Nudge {nudge.user?.name || nudge.user?.email}</DialogTitle>
            <DialogDescription>
              Drop a friendly message. It lands in their notification bell + Messages thread instantly.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={nudge.text}
            onChange={(e) => setNudge((n) => ({ ...n, text: e.target.value }))}
            placeholder="👋 Hi! Saw you're here — need anything today?"
            rows={4}
            data-testid="nudge-text"
          />
          <p className="text-xs text-slate-400">Leave empty to send our default hello.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNudge({ open: false, user: null, text: "", sending: false })}>Cancel</Button>
            <Button onClick={sendNudge} disabled={nudge.sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="nudge-send">
              {nudge.sending ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
              Send nudge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="p-4 rounded-2xl border-slate-100 flex items-center gap-3" data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl bg-slate-50 flex items-center justify-center ${color}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-2xl font-black text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-bold">{label}</p>
      </div>
    </Card>
  );
}

function Section({ title, empty, rows, onNudge }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-100">
      <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">{empty}</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((u) => <Row key={u.user_id} u={u} onNudge={onNudge} />)}
        </div>
      )}
    </Card>
  );
}

function Row({ u, onNudge }) {
  const roleBadge = {
    client: { label: "Client", cls: "bg-emerald-100 text-emerald-700" },
    team_member: { label: "Team", cls: "bg-sky-100 text-sky-700" },
    credit_specialist: { label: "Specialist", cls: "bg-sky-100 text-sky-700" },
    admin: { label: "Admin", cls: "bg-amber-100 text-amber-700" },
    super_admin: { label: "Super Admin", cls: "bg-amber-100 text-amber-700" },
  }[u.role] || { label: u.role, cls: "bg-slate-100 text-slate-600" };

  return (
    <div className="py-3 flex items-center gap-3" data-testid={`live-row-${u.user_id}`}>
      <div className="relative">
        {u.avatar_url ? (
          <img src={u.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black">
            {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 ring-2 ring-white" />
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-bold text-slate-900 truncate">{u.name || u.email}</p>
          <Badge className={`${roleBadge.cls} border-0 text-[10px] uppercase tracking-widest`}>{roleBadge.label}</Badge>
          {u.tab_count > 1 && (
            <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px]">{u.tab_count} tabs</Badge>
          )}
        </div>
        <p className="text-xs text-slate-500 truncate font-mono">{u.path || "—"}</p>
      </div>

      {u.role === "client" && (
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={() => onNudge(u)} data-testid={`nudge-${u.user_id}`}>
            <Zap size={12} className="mr-1" /> Nudge
          </Button>
          <Link to={`/admin?user_id=${u.user_id}`}>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`message-${u.user_id}`}>
              <MessageSquare size={12} className="mr-1" /> Message
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

function ClientTicker({ events }) {
  const rel = (at) => {
    const s = Math.max(0, Math.floor((Date.now() - at) / 1000));
    if (s < 5)  return "just now";
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ago`;
  };
  return (
    <Card className="p-3 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0 overflow-hidden" data-testid="client-ticker">
      <div className="flex items-center gap-3 min-h-8">
        <span className="text-[10px] uppercase tracking-[0.32em] font-black text-emerald-300 shrink-0 inline-flex items-center gap-1.5">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          Client Ticker
        </span>
        <div className="flex-1 flex items-center gap-2 overflow-x-auto min-w-0">
          {events.length === 0 ? (
            <span className="text-xs text-slate-400 italic">Watching for client arrivals & departures…</span>
          ) : (
            events.slice(0, 12).map((ev) => {
              const u = ev.user || {};
              const inTone = ev.dir === "in"
                ? "border-emerald-400/40 bg-emerald-500/10"
                : "border-slate-500/40 bg-slate-500/10 opacity-70";
              return (
                <span
                  key={ev.id}
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold inline-flex items-center gap-1.5 whitespace-nowrap ${inTone}`}
                  data-testid={`client-ticker-${ev.dir}-${u.user_id}`}
                >
                  {ev.dir === "in"
                    ? <LogIn size={11} className="text-emerald-400" />
                    : <LogOut size={11} className="text-slate-400" />}
                  <span className="text-emerald-200 truncate max-w-[10rem]">{u.name || u.email || "Client"}</span>
                  <span className="text-slate-400">{ev.dir === "in" ? "on" : "off"} · {rel(ev.at)}</span>
                </span>
              );
            })
          )}
        </div>
      </div>
    </Card>
  );
}

